/**
 * Timestamp in milliseconds of a card date as the storymap store holds it, or null when the value is missing.
 *
 * A card that was never updated has no `lastUpdatedAt`; the store holds that as `null` (cards created in this session
 * or live-created ones) or as `new Date(null)`, the Unix epoch, after a board load (`initStorymap.ts`). Values received
 * through the realtime actions can still be ISO strings.
 */
const toTime = (value: Date | string | null | undefined): number | null => {
    if (value == null) {
        return null;
    }

    const time = value instanceof Date ? value.getTime() : new Date(value).getTime();

    return isNaN(time) || time === 0 ? null : time;
};

/**
 * "Created or updated" filter (owner decision #37, parity with Classic's `CardModelMapper` `updatedOrCreatedAt`): the
 * card's latest change — `lastUpdatedAt` when the card was updated, otherwise `createdAt` — is within [from, to).
 * A missing bound leaves that side of the period open.
 */
export const isLastChangeWithinPeriod = (
    createdAt: Date | string | null | undefined,
    lastUpdatedAt: Date | string | null | undefined,
    from: Date | string | null | undefined,
    to: Date | string | null | undefined
): boolean => {
    const latestChange = toTime(lastUpdatedAt) ?? toTime(createdAt);
    if (latestChange == null) {
        return false;
    }

    const fromTime = toTime(from);
    const toTimeValue = toTime(to);

    return (fromTime == null || latestChange >= fromTime) && (toTimeValue == null || latestChange < toTimeValue);
};
